import React, { useState, useEffect } from 'react';

const AppraisalHistory = () => {
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  // Mock data - in a real app this would come from an API
  useEffect(() => {
    const mockAppraisals = [
      {
        id: 1,
        date: '2023-10-15',
        type: 'Annual Review',
        rating: 'Exceeds Expectations',
        salaryBefore: '$85,000',
        salaryAfter: '$92,000',
        increment: '8.2%',
        manager: 'Sarah Johnson',
        status: 'Completed',
        details: 'Exceptional performance in Q2 and Q3. Took leadership on the Nexus project and delivered ahead of schedule. Showed strong mentorship skills with junior team members.',
        employeeComments: 'I appreciate the recognition for the Nexus project. Looking forward to taking on more leadership responsibilities.',
        goals: 'Lead a cross-functional team, Obtain AWS Certification, Mentor two junior developers'
      },
      {
        id: 2,
        date: '2022-10-10',
        type: 'Annual Review',
        rating: 'Meets Expectations',
        salaryBefore: '$78,000',
        salaryAfter: '$85,000',
        increment: '9.0%',
        manager: 'Michael Chen',
        status: 'Completed',
        details: 'Consistent performance throughout the year. Met all project deadlines. Good collaboration with team members.',
        employeeComments: 'Happy with my progress this year. Would like more opportunities for technical training.',
        goals: 'Complete advanced React course, Improve documentation skills, Participate in hiring process'
      },
      {
        id: 3,
        date: '2023-04-15',
        type: 'Mid-Year Review',
        rating: 'Exceeds Expectations',
        salaryBefore: '$85,000',
        salaryAfter: '$85,000',
        increment: '0%',
        manager: 'Sarah Johnson',
        status: 'Completed',
        details: 'Strong performance in the first half of the year. Successfully implemented the new authentication system.',
        employeeComments: 'Thank you for the feedback. Disappointed that mid-year reviews don\'t include salary adjustments.',
        goals: 'Continue current performance, Document the auth system, Share knowledge with team'
      },
      {
        id: 4,
        date: '2021-10-05',
        type: 'Annual Review',
        rating: 'Meets Expectations',
        salaryBefore: '$72,000',
        salaryAfter: '$78,000',
        increment: '8.3%',
        manager: 'Robert Williams',
        status: 'Completed',
        details: 'Good first full year with the company. Adapted well to company processes and culture.',
        employeeComments: 'Enjoying my time here. Looking forward to taking on more complex projects next year.',
        goals: 'Master company frameworks, Get to know other departments, Improve presentation skills'
      },
      {
        id: 5,
        date: '2023-11-20',
        type: 'Performance Improvement',
        rating: 'Needs Improvement',
        salaryBefore: '$92,000',
        salaryAfter: '$92,000',
        increment: '0%',
        manager: 'Sarah Johnson',
        status: 'In Progress',
        details: 'Missed deadlines on the last two projects. Need to focus on time management and communication.',
        employeeComments: 'I have been dealing with some personal issues that affected my work. Will improve moving forward.',
        goals: 'Meet all project deadlines for next quarter, Improve communication with manager, Complete time management workshop'
      }
    ];

    // Simulate API loading
    setTimeout(() => {
      // Apply sorting
      const sortedAppraisals = [...mockAppraisals].sort((a, b) => {
        if (sortBy === 'date') {
          return new Date(b.date) - new Date(a.date);
        } else if (sortBy === 'rating') {
          const ratingOrder = {
            'Exceeds Expectations': 3,
            'Meets Expectations': 2,
            'Needs Improvement': 1
          };
          return ratingOrder[b.rating] - ratingOrder[a.rating];
        }
        return 0;
      });
      
      setAppraisals(sortedAppraisals);
      setLoading(false);
    }, 1500);
  }, [sortBy]);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return '#10b981';
      case 'In Progress': return '#3b82f6';
      case 'Pending': return '#f59e0b';
      case 'Cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getRatingColor = (rating) => {
    switch (rating) {
      case 'Exceeds Expectations': return '#10b981';
      case 'Meets Expectations': return '#3b82f6';
      case 'Needs Improvement': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const filteredAppraisals = filter === 'all' 
    ? appraisals 
    : appraisals.filter(a => a.type === filter || a.status === filter);

  const containerStyle = {
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    color: '#2d3748',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    minHeight: '100vh'
  };

  const headerStyle = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '25px',
    borderRadius: '12px',
    marginBottom: '30px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    animation: 'fadeIn 0.8s ease-out'
  };

  const filtersStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '10px',
    background: 'rgba(255, 255, 255, 0.8)',
    padding: '15px',
    borderRadius: '10px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
  };

  const buttonGroupStyle = {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap'
  };

  const filterButtonStyle = (isActive) => ({
    padding: '8px 16px',
    borderRadius: '20px',
    border: 'none',
    background: isActive 
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
      : '#e2e8f0',
    color: isActive ? 'white' : '#4a5568',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: isActive ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none'
  });

  const cardStyle = {
    background: 'linear-gradient(120deg, #fdfbfb 0%, #ebedee 100%)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.04)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    overflow: 'hidden',
    position: 'relative'
  };

  const cardHoverStyle = {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
  };

  const statusStyle = (status) => ({
    display: 'inline-block',
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
    backgroundColor: getStatusColor(status)
  });

  const ratingStyle = (rating) => ({
    display: 'inline-block',
    padding: '5px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
    backgroundColor: getRatingColor(rating),
    marginLeft: '10px'
  });

  const detailStyle = {
    padding: '15px',
    marginTop: '15px',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '8px',
    borderLeft: '4px solid #667eea',
    animation: 'slideDown 0.4s ease-out'
  };

  const loadingStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px'
  };

  const spinnerStyle = {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  const salaryChangeStyle = (increment) => ({
    color: increment === '0%' ? '#718096' : '#10b981',
    fontWeight: '600'
  });

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideDown {
            from { opacity: 0; max-height: 0; }
            to { opacity: 1; max-height: 500px; }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <div style={headerStyle}>
        <h1 style={{ margin: '0', fontSize: '28px', fontWeight: '600' }}>Appraisal History</h1>
        <p style={{ margin: '10px 0 0', opacity: '0.9' }}>
          Track your performance reviews, career progression, and salary history
        </p>
      </div>

      <div style={filtersStyle}>
        <div>
          <label style={{ fontWeight: '600', marginRight: '10px' }}>Filter by:</label>
          <div style={buttonGroupStyle}>
            <button 
              style={filterButtonStyle(filter === 'all')}
              onClick={() => setFilter('all')}
            >
              All Appraisals
            </button>
            <button 
              style={filterButtonStyle(filter === 'Annual Review')}
              onClick={() => setFilter('Annual Review')}
            >
              Annual Reviews
            </button>
            <button 
              style={filterButtonStyle(filter === 'Mid-Year Review')}
              onClick={() => setFilter('Mid-Year Review')}
            >
              Mid-Year Reviews
            </button>
            <button 
              style={filterButtonStyle(filter === 'Completed')}
              onClick={() => setFilter('Completed')}
            >
              Completed
            </button>
          </div>
        </div>
        
        <div>
          <label style={{ fontWeight: '600', marginRight: '10px' }}>Sort by:</label>
          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: '8px 16px',
              borderRadius: '20px',
              border: '2px solid #e2e8f0',
              background: 'white',
              fontWeight: '600',
              color: '#4a5568'
            }}
          >
            <option value="date">Date (Newest First)</option>
            <option value="rating">Rating</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
        </div>
      ) : (
        <div>
          {filteredAppraisals.map((appraisal) => (
            <div 
              key={appraisal.id}
              style={{
                ...cardStyle,
                ...(expandedId === appraisal.id ? cardHoverStyle : {})
              }}
              onClick={() => toggleExpand(appraisal.id)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.08)';
              }}
              onMouseLeave={(e) => {
                if (expandedId !== appraisal.id) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.04)';
                }
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ margin: '0 0 8px', color: '#2d3748' }}>
                    {appraisal.type} - {appraisal.date}
                    <span style={ratingStyle(appraisal.rating)}>{appraisal.rating}</span>
                  </h3>
                  <p style={{ margin: '0', color: '#667eea', fontWeight: '600' }}>
                    Manager: {appraisal.manager}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={statusStyle(appraisal.status)}>{appraisal.status}</div>
                  <p style={{ margin: '8px 0 0', color: '#718096', fontSize: '14px' }}>
                    Salary: <span style={{textDecoration: 'line-through'}}>{appraisal.salaryBefore}</span> → <span style={{fontWeight: '600'}}>{appraisal.salaryAfter}</span>
                    <span style={salaryChangeStyle(appraisal.increment)}> ({appraisal.increment})</span>
                  </p>
                </div>
              </div>
              
              {expandedId === appraisal.id && (
                <div style={detailStyle}>
                  <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                    <div>
                      <h4 style={{margin: '0 0 8px', color: '#667eea'}}>Manager's Comments</h4>
                      <p style={{margin: 0, lineHeight: '1.5'}}>{appraisal.details}</p>
                    </div>
                    
                    <div>
                      <h4 style={{margin: '0 0 8px', color: '#667eea'}}>Employee's Comments</h4>
                      <p style={{margin: 0, lineHeight: '1.5', fontStyle: 'italic'}}>{appraisal.employeeComments}</p>
                    </div>
                    
                    <div>
                      <h4 style={{margin: '0 0 8px', color: '#667eea'}}>Goals for Next Period</h4>
                      <p style={{margin: 0, lineHeight: '1.5'}}>{appraisal.goals}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppraisalHistory;
